import MenuUnfoldOutlinedIcon from '@ant-design/icons/MenuUnfoldOutlined'

import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import { alpha, useTheme } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'

import { RoleGuard, useMe } from '@shared/providers/src/MeProvider'
import { UserRole } from '@shared/utils'

import { useLayoutState } from '@providers/LayoutStateProvider'

import Availability from './Content/Availability'
import CBOSupportMap from './Content/CBOSupportMap'
import Environment from './Content/Environment'
import Mentions from './Content/Mentions'
import Notifications from './Content/Notifications'
import OnDemandPending from './Content/OnDemandPending'
import PagesHistory from './Content/PagesHistory'
import PhoneCall from './Content/PhoneCall'
import Profile from './Content/Profile'
import QPhrases from './Content/QPhrases'
import Schedule from './Content/Schedule'
import Search from './Content/Search'
import Settings from './Content/Settings'
import SOPs from './Content/SOPs'
import ThemeToggle from './Content/ThemeToggle'

export default function Header() {
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, isSmallScreen } = useLayoutState()

  const me = useMe()
  const theme = useTheme()
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const size = matchDownSm ? 'small' : 'medium'

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={(theme) => ({
        backgroundColor: alpha(theme.palette.background.default, 0.8),
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${theme.palette.divider}`,
        zIndex: theme.zIndex.drawer - 1,
        ...(!isSmallScreen && {
          ml: `${sidebarCollapsed ? theme.mixins.sidebar.miniWidth : theme.mixins.sidebar.fullWidth}px`,
          width: `calc(100% - ${sidebarCollapsed ? theme.mixins.sidebar.miniWidth : theme.mixins.sidebar.fullWidth}px)`,
        }),
        ...(isSmallScreen && {
          ml: 0,
          width: '100%',
        }),
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      })}
    >
      <Toolbar variant="dense" sx={{ gap: 0.5 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, flex: 1 }}>
          {isSmallScreen && !sidebarOpen && (
            <IconButton size="small" onClick={setSidebarOpen} sx={{ color: 'text.secondary' }}>
              <MenuUnfoldOutlinedIcon style={{ fontSize: '1rem' }} />
            </IconButton>
          )}
          <Search size={size} />
          <PagesHistory size={size} />
          <ThemeToggle size={size} />
          {!matchDownSm && me.role === UserRole.Provider && <OnDemandPending />}
          {!matchDownSm && <Environment />}
        </Stack>

        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
          <RoleGuard prohibited={[UserRole.Compliance]}>
            <QPhrases size={size} />
          </RoleGuard>
          <RoleGuard
            prohibited={[
              UserRole.CBO,
              UserRole.Pharmacy,
              UserRole.PharmacyManager,
              UserRole.Manager,
              UserRole.Manager340B,
              UserRole.Compliance,
            ]}
          >
            <PhoneCall size={size} />
          </RoleGuard>
          <RoleGuard prohibited={[UserRole.CBO, UserRole.Compliance]}>
            <CBOSupportMap size={size} />
          </RoleGuard>
          <RoleGuard prohibited={[UserRole.CBO, UserRole.Pharmacy, UserRole.PharmacyManager]}>
            <SOPs size={size} />
          </RoleGuard>
          <RoleGuard prohibited={[UserRole.CBO, UserRole.Compliance]}>
            <Schedule size={size} />
          </RoleGuard>
          <RoleGuard allowed={[UserRole.Provider]}>
            <Availability size={size} />
            <Settings size={size} />
          </RoleGuard>
          <RoleGuard prohibited={[UserRole.Compliance]}>
            <Mentions size={size} />
          </RoleGuard>
          <Notifications size={size} />
          <Profile size={size} />
        </Stack>
      </Toolbar>
    </AppBar>
  )
}
